/**
 * Auth0 Session Management Utilities
 * Helps clear cached sessions and force fresh logins
 */

/**
 * Clear all Auth0-related data from browser storage
 * This should only be called explicitly by user action, not automatically
 */
export function clearAuth0Cache() {
  console.log('Clearing Auth0 cache...');
  
  // Clear only specific Auth0 SPA JS cache keys to avoid interfering with active sessions
  const keysToRemove = [
    '@@auth0spajs@@::' + import.meta.env.VITE_AUTH0_CLIENT_ID + '::' + import.meta.env.VITE_AUTH0_AUDIENCE + '::openid profile email offline_access',
    '_auth0:' + import.meta.env.VITE_AUTH0_CLIENT_ID,
    'auth0:' + import.meta.env.VITE_AUTH0_CLIENT_ID,
  ];
  
  // Clear localStorage selectively
  Object.keys(localStorage).forEach(key => {
    // Only remove keys that match our specific patterns
    if (key.includes('@@auth0spajs@@') || 
        key.includes('_legacy_auth0') ||
        keysToRemove.some(pattern => key.includes(pattern))) {
      console.log(`Removing localStorage key: ${key}`);
      localStorage.removeItem(key);
    }
  });

  // Clear sessionStorage selectively
  Object.keys(sessionStorage).forEach(key => {
    if (key.includes('@@auth0spajs@@') || 
        key.includes('_legacy_auth0') ||
        keysToRemove.some(pattern => key.includes(pattern))) {
      console.log(`Removing sessionStorage key: ${key}`);
      sessionStorage.removeItem(key);
    }
  });

  console.log('Auth0 cache cleared');
}

/**
 * Force a complete logout from Auth0
 */
export function forceAuth0Logout(auth0Domain: string) {
  // Clear local cache first
  clearAuth0Cache();
  
  // Construct logout URL to clear Auth0 session
  const logoutUrl = `https://${auth0Domain}/v2/logout?client_id=${import.meta.env.VITE_AUTH0_CLIENT_ID}&returnTo=${encodeURIComponent(window.location.origin)}`;
  
  // Navigate to Auth0 logout endpoint
  window.location.href = logoutUrl;
}

/**
 * Get the correct redirect URI based on current environment
 */
export function getRedirectUri(): string {
  // Always use current origin in development
  if (import.meta.env.MODE === 'development') {
    return `${window.location.origin}/callback`;
  }

  // Netlify deploy previews have URLs like: <hash>--<site>.netlify.app
  // Use dynamic origin so the callback matches the preview URL
  const hostname = window.location.hostname;
  if (hostname.includes('--') && hostname.endsWith('.netlify.app')) {
    return `${window.location.origin}/callback`;
  }

  // Production: use environment variable if set, otherwise current origin
  return import.meta.env.VITE_AUTH0_REDIRECT_URI || `${window.location.origin}/callback`;
}

/**
 * Check if redirect URI matches current environment
 */
export function validateRedirectUri(configuredUri: string): boolean {
  const currentOrigin = window.location.origin;
  const expectedUri = `${currentOrigin}/callback`;
  
  if (configuredUri !== expectedUri) {
    console.warn(`Redirect URI mismatch!
      Configured: ${configuredUri}
      Expected: ${expectedUri}
      Current Origin: ${currentOrigin}
    `);
    return false;
  }
  
  return true;
}